import React, { useMemo } from 'react';
import { Timeline } from 'antd';
import type { TimelineProps as AntTimelineProps } from 'antd';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import type { BaseComponentProps, DataSetConfig } from '../../types/common';

export interface AndTimelineProps extends AntTimelineProps, BaseComponentProps, DataSetConfig {}

export const AndTimeline: React.FC<AndTimelineProps> = ({
  id, sessionId, develop, items: propItems, children, dataSet, dataSetField,
  // @ts-ignore
  remoteUrl, 
  mode,
  ...restProps
}) => {
  const getDataSet = useAndStore((state) => state.getDataSet);
  const getProps = useAndStore((state) => state.getProps);
  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );

  const storeProps = useMemo(() => {
    if (sessionId && id) return getProps(sessionId, id);
    return null;
  }, [sessionId, id, getProps]);

  const storeValue = useMemo(() => {
    if (dataSetKey && dataSetField) return getDataSet(dataSetKey, dataSetField);
    return undefined;
  }, [dataSetKey, dataSetField, getDataSet]);

  // 设计器模式(develop)：优先 store，无则回退到静态 items；运行时且配置了 dataSet：仅用 store，不回退到静态 items
  const computedItems = useMemo(() => {
    let items: any;
    const storeItems =
      storeValue !== undefined && storeValue !== null
        ? (Array.isArray(storeValue) ? storeValue : typeof storeValue === 'string' ? (() => { try { return JSON.parse(storeValue); } catch { return undefined; } })() : undefined)
        : storeProps?.items;
    if (develop) {
      items = storeItems ?? propItems;
    } else if (dataSetKey) {
      items = storeItems ?? [];
    } else {
      items = propItems;
    }

    if (Array.isArray(items)) {
      return items.map((item: any) => {
        // Fix deprecation warnings: label -> title, children -> content
        const newItem = { ...item };
        if (newItem.label && !newItem.title) {
          newItem.title = newItem.label;
          delete newItem.label;
        }
        if (newItem.children && !newItem.content) {
          newItem.content = newItem.children;
          delete newItem.children;
        }
        return newItem;
      });
    }
    return items;
  }, [develop, dataSetKey, storeValue, storeProps?.items, propItems]);

  const mergedProps = useMemo(() => {
    const baseProps = { ...restProps };
    if (storeProps && typeof storeProps === 'object') {
      Object.keys(storeProps).forEach((key) => {
        if (key !== 'items' && storeProps[key] !== undefined && storeProps[key] !== null) {
          (baseProps as any)[key] = storeProps[key];
        }
      });
    }
    return baseProps;
  }, [restProps, storeProps]);

  // Map legacy 'left'/'right' to 'start'/'end' to avoid Antd warning
  // @ts-ignore
  const mapMode = (m: string | undefined): AntTimelineProps['mode'] => {
     if (m === 'left') return 'left'; // Try keeping 'left' if 'start' not supported or fallback
     // Actually warning says use start/end.
     if (m === 'left') return 'start' as any;
     if (m === 'right') return 'end' as any;
     return m as any;
  };
  
  // Actually, let's just force start/end if we are sure Antd version supports it.
  // Given the warning "Please use mode=start|end", it supports it.
  
  const finalMode = useMemo(() => {
     if (mode === 'left') return 'start';
     if (mode === 'right') return 'end';
     return mode;
  }, [mode]);

  return (
    <Timeline 
      {...mergedProps} 
      // @ts-ignore
      mode={finalMode}
      items={computedItems}
    >
      {children}
    </Timeline>
  );
};


export default AndTimeline;

